import React, { useState } from 'react';
import cardPay from '../../assets/images/card-payment.svg';
import { CheckCircle, Calendar } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { delteMembership, updatePaymentMethod } from "../../api/PayApi";
import { readAuthMeta } from '../../context/AuthSync';
import './MembershipCard.css';
import LoadingModal from '../membership/LoadingModal';

const PremiumIcon = () => (
  <svg width="36" height="36" viewBox="0 0 36 36" fill="none">
    <circle cx="17" cy="17" r="16" fill="#F6B800"/>
    <circle cx="17" cy="17" r="12" fill="white"/>
    <circle cx="17" cy="17" r="9" fill="#F6B800"/>
    <polygon points="17,12.5 20,17 17,21.5 14,17" fill="white"/>
  </svg>
);

const PremiumIconList = ({ size = 20, color = "#F6B800" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <circle
      cx="12"
      cy="12"
      r="10"
      stroke={color}
      strokeWidth="2"
      fill="none"
    />
    <path
      d="M8 12.5L10.5 15L16 9.5"
      stroke={color}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const MembershipCardCard = ({invoce}) => {

  const [modalState, setModalState] = useState({
    isOpen: false,
    status: 'loading',
    message: ''
  });
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [showPaymentDetails, setShowPaymentDetails] = useState(false); // Vista de detalles de pago
  const [showEditForm, setShowEditForm] = useState(false); // Formulario de edición
  const [paymentForm, setPaymentForm] = useState({
    cardNumber: '',
    expireDate: '',
    cvv: '',
    cardName: ''
  });

  const amount = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(Number(invoce.planAmount)/ 100);  

  const navigate = useNavigate();
  const status = String(invoce?.status || '').toLowerCase();

  // Compute days remaining and formatted next billing date
  let daysRemaining = 0;
  let formattedNextBilling = '';
  try {
    if (invoce?.nextBillingDate) {
      const raw = invoce.nextBillingDate;
      const ms =
        typeof raw === 'number'
          ? (raw < 1e12 ? raw * 1000 : raw)
          : Date.parse(raw);
      if (!Number.isNaN(ms)) {
        const nextDate = new Date(ms);
        const now = new Date();
        const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        const startOfNext = new Date(nextDate.getFullYear(), nextDate.getMonth(), nextDate.getDate());
        const diffMs = startOfNext - startOfToday;
        daysRemaining = Math.max(0, Math.ceil(diffMs / 86400000));
        formattedNextBilling = nextDate.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });
      }
    }
  } catch (_) {
    // Keep defaults on parsing issues
  }
  const daysLabel = daysRemaining === 1 ? 'day' : 'days';

  const freeBenefits = [
  "Access to the introductory video and the possibility to register",
  "Access to the general layout of the tool.",
  "Limited view: only 1 city and 1 suburb for you to test the functionality.",
];

  
  const features = [
    "States",
    "Cities",
    "Suburbs",
    "Price Cycle View",
    "Graphs and Tables",
    '"Big" View (Expanded View)',
  ];
  
  async function handleSubmit() {
   
    const meta = readAuthMeta();
    const email = meta?.email || meta?.user?.email || null;
    if (!email) return; // no hay usuario autenticado

    setModalState({
      isOpen: true,
      status: 'loading',
      message: 'Processing payment...',
      modalOpen: true
    });

    try {
      const response = await delteMembership(invoce.tokenSubscription);

      if(response.status !== 200) {
        throw new Error("Delete membership");
      } 

      setModalState({
        isOpen: true,
        status: 'success',
        message: '¡Delete membership! 🎉',
        modalOpen: true
      });

    } catch (error) {
      setModalState({
        isOpen: true,
        status: 'error',
        message: 'Error al procesar el pago. Por favor, intente nuevamente.',
        modalOpen: true
      });

      console.error("Error al consumir el endpoint:", error);
    }
    
  };

  // Función para mostrar los detalles de pago en modal (paso 1)
  const handleShowPaymentDetails = () => {
    setShowPaymentDetails(true);
    setModalState({
      isOpen: true,
      status: 'info', // Nuevo estado para mostrar información
      message: ''
    });
  };

  // Función para mostrar el formulario de edición (paso 2 - sin consumir API aún)
  const handleShowEditForm = () => {
    setShowEditForm(true);
  };

  const handleCancelEdit = () => {
    setShowPaymentDetails(false);
    setShowEditForm(false);
    setModalState({ isOpen: false, status: 'loading', message: '' });
    setPaymentForm({
      cardNumber: '',
      expireDate: '',
      cvv: '',
      cardName: ''
    });
  };

  const handlePaymentFormChange = (e) => {
    const { name, value } = e.target;
    let formattedValue = value;

    // Formatear el número de tarjeta
    if (name === 'cardNumber') {
      formattedValue = value.replace(/\D/g, '').slice(0, 16);
    }
    // Formatear la fecha de expiración
    else if (name === 'expireDate') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
      if (formattedValue.length >= 2) {
        formattedValue = formattedValue.slice(0, 2) + '/' + formattedValue.slice(2);
      }
    }
    // Formatear CVV
    else if (name === 'cvv') {
      formattedValue = value.replace(/\D/g, '').slice(0, 4);
    }

    setPaymentForm({
      ...paymentForm,
      [name]: formattedValue
    });
  };

  const handleUpdatePayment = async () => {
    const meta = readAuthMeta();
    const email = meta?.email || meta?.user?.email || null;
    if (!email) return; // no hay usuario autenticado

    setShowEditForm(false);
    // Validaciones básicas
    if (!paymentForm.cardNumber || paymentForm.cardNumber.length < 13) {
      alert('Invalid card number');
      return;
    }
    if (!paymentForm.expireDate || !/^\d{2}\/\d{2}$/.test(paymentForm.expireDate)) {
      alert('Invalid expiration date (MM/YY)');
      return;
    }
    if (!paymentForm.cvv || paymentForm.cvv.length < 3) {
      alert('Invalid CVV');
      return;
    }
    if (!paymentForm.cardName || paymentForm.cardName.trim().split(/\s+/).length < 2) {
      alert('Please enter the full name on the card');
      return;
    }

    setModalState({
      isOpen: true,
      status: 'loading',
      message: 'Updating payment method...'
    });

    try {
      // Llamada al API para actualizar el método de pago
     const response = await updatePaymentMethod(email, paymentForm);

      if(response.status !== 200) {
        const error = await response.json().catch(() => ({}));
        console.error("Error during payment processing:",  error);
        
        if (error.messages && error.messages.length > 0) {
          const apiMessage = error.messages[0].message;
          setModalState({
            isOpen: true,
            status: 'error',
            message: 'Error:' + apiMessage,
            modalOpen: true
          });
        } else {
          setModalState({
            isOpen: true,
            status: 'error',
            message: 'Payment processing error. Please try again.',
            modalOpen: true
          });
        }
        return;
        
      } 


      setModalState({
        isOpen: true,
        status: 'success',
        message: 'Payment method updated successfully! 🎉'
      });
      
      
    } catch (error) {
      setModalState({
        isOpen: true,
        status: 'error',
        message: 'Error updating payment method. Please try again.'
      });
      console.error("Error updating payment:", error);
    }
  };

  


  return (
    <div className="membershipCard-card">
    {status === 'active'  && (
      <div>
        <div className="membershipCard-card__section">
          <div className="membershipCard-card__value">Your membership</div>
          <div className="premium-box">
            <div className="premium-icon">
              <PremiumIcon />
            </div>
            <div>
              <div className="premium-title">Premium Membership</div>
              <div className="premium-desc">Full access to all sections and functionalities.</div>
            </div>
          </div>
        </div>

        <div className="membershipCard-card__section">
          <div className="membershipCard-card__row vertical-align">
            <div className="membershipCard-card__column-left">
              <div className="membershipCard-card__title">Included in your membership</div>
              <div className="membershipCard-card__desc">
                With this plan, you get unlimited access to all data and visualization tools, allowing you to dive deep into every geographical level and make the most of reports in graphs, tables, and price cicles.
              </div>
            </div>
          </div>
          <div className="membershipCard-card__row vertical-align">
            <div className="membershipCard-card__column-left">
              <ul className="feature-list">
                {features.map((item, index) => (
                  <li key={index} className="feature-item">
                    <PremiumIconList size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        
        <div className="membershipCard-card__section">
          <div className="membershipCard-card__row vertical-align">
            <div className="membershipCard-card__column-left">
              <div className="membershipCard-card__title">Billing and payment</div>
            </div>
          </div>
        </div>

        <div className="security-card__divider" />

        <div className="membershipCard-card__section">
          <div className="membershipCard-card__row vertical-align">
            <div className="membershipCard-card__column-left">
              <div className="membershipCard-card__desc">
                <img src={cardPay} alt="Card icon" /> {invoce.scheme} ending in {invoce.numberCard}
              </div>
            </div>
            <div className="membershipCard-card__column-right">
              <button className="btn edit-btn" onClick={handleShowPaymentDetails}>Edit billing and payment</button>
            </div>
          </div>
        </div>

        <div className="security-card__divider" />

        <div className="membershipCard-card__section">
          <div className="membershipCard-card__row vertical-align">
            <div className="membershipCard-card__column-left">
              <div className="membershipCard-card__desc">
                <Calendar className="confirm-calendar-icon" size={18} /> {amount}AUD/month - Next payment on {invoce.nextBillingDate}
              </div>
            </div>
            {invoce.tokenSubscription !== null  && (  
            <div className="membershipCard-card__column-right">
              <button className="btn cancel-btn" onClick={() => setConfirmOpen(true)}>
                <span className="cancel-icon">✕</span>
                Cancel your membership
              </button>
            </div>
            )}
          </div>
        </div>
      </div>

      )}

  {status === 'free'  && (
      <div>
        <div className="membership-card membership-card--free">
          <div className="membership-card__price">
            $0/month – Free
          </div>
          <hr className="membership-card__divider" />
          <div className="membership-card__badge membership-card__badge--free">
            <div>
              <div className="membership-card__badge-title">Free Membership</div>
              <div className="membership-card__badge-desc">
                Ideal for familiarising yourself with the platform
              </div>
            </div>
          </div>
          <div className="membership-card__includes">
            <strong>Includes:</strong>
            <div className="membership-card__desc">
              For users to be able to explore the interface and understand how it works at no cost
            </div>
            <ul>
              {freeBenefits.map((b, i) => (
                <li key={i}>
                  <CheckCircle className="membership-card__check membership-card__check--free" size={23} strokeWidth={2.3} />
                  {b}
                </li>
              ))}
            </ul>
          </div>
          <div className="membership-card__actions">
            <button
              className="membership-card__cta membership-card__cta--free"
              onClick={() => navigate('/membership')}
            >
              Upgrade to premium
            </button>
          </div>
        </div>
      </div>
      
      )}

      {/* 🌀 Modal de carga */}
      <LoadingModal 
        isOpen={modalState.isOpen}
        status={modalState.status}
        message={modalState.message}
        onClose={() => {
          setModalState({ ...modalState, isOpen: false });
          // Si fue un update exitoso, redirigir al dashboard
          if (modalState.status === 'success' && modalState.message.includes('Payment method')) {
            navigate('/dashboard');
          }
        }}
      >
        {/* Contenido personalizado cuando status === 'info' */}
        {modalState.status === 'info' && showPaymentDetails && !showEditForm && (
          <div>
            <div className="membershipCard-card__section">
              <div className="membershipCard-card__row vertical-align">
                <div className="membershipCard-card__column-left">
                  <div className="membershipCard-card__title">Billing and payment</div>
                </div>
              </div>
            </div>
            <div className="security-card__divider" />
            <div className="membershipCard-card__section">
              <div className="membershipCard-card__row vertical-align">
                <div className="membershipCard-card__column-left">
                  <div className="membershipCard-card__desc">
                    <img src={cardPay} alt="Card icon" /> {invoce.scheme} ending in {invoce.numberCard}
                  </div>
                </div>
              </div>
            </div>
            <div className="security-card__divider" />
            <div style={{ display: 'flex', gap: '12px', marginTop: '16px', justifyContent: 'flex-end' }}>
              <button className="btn edit-btn" onClick={handleCancelEdit}>Cancel</button>
              <button className="btn confirm-cancel-btn" onClick={handleShowEditForm}>Edit</button>
            </div>
          </div>
        )}

        {/* Formulario de edición dentro de la modal */}
        {modalState.status === 'info' && showEditForm && (
          <div>
            <div className="membershipCard-card__section">
              <div className="membershipCard-card__row vertical-align">
                <div className="membershipCard-card__column-left">
                  <div className="membershipCard-card__title">Edit payment method</div>
                </div>
              </div>
            </div>
            <div className="security-card__divider" />
            <div className="membershipCard-card__section">
              <div className="membershipCard-card__desc" style={{ marginBottom: '16px' }}>
                <img src={cardPay} alt="Card icon" style={{ marginRight: '8px' }} />
                Current: {invoce.scheme} ending in {invoce.numberCard}
              </div>

              <div className="payment-form-group">
                <label className="payment-form-label">Card Number</label>
                <input
                  type="text"
                  name="cardNumber"
                  className="payment-form-input"
                  placeholder="1234 5678 9012 3456"
                  value={paymentForm.cardNumber}
                  onChange={handlePaymentFormChange}
                  maxLength={16}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px' }}>
                <div className="payment-form-group" style={{ flex: 1 }}>
                  <label className="payment-form-label">Expiration Date</label>
                  <input
                    type="text"
                    name="expireDate"
                    className="payment-form-input"
                    placeholder="MM/YY"
                    value={paymentForm.expireDate}
                    onChange={handlePaymentFormChange}
                    maxLength={5}
                  />
                </div>

                <div className="payment-form-group" style={{ flex: 1 }}>
                  <label className="payment-form-label">CVV</label>
                  <input
                    type="text"
                    name="cvv"
                    className="payment-form-input"
                    placeholder="123"
                    value={paymentForm.cvv}
                    onChange={handlePaymentFormChange}
                    maxLength={4}
                  />
                </div>
              </div>

              <div className="payment-form-group">
                <label className="payment-form-label">Cardholder Name</label>
                <input
                  type="text"
                  name="cardName"
                  className="payment-form-input"
                  placeholder="John Doe"
                  value={paymentForm.cardName}
                  onChange={handlePaymentFormChange}
                />
              </div>

              <div style={{ display: 'flex', gap: '12px', marginTop: '16px', justifyContent: 'flex-end' }}>
                <button className="btn edit-btn" onClick={handleCancelEdit}>Cancel</button>
                <button className="btn confirm-cancel-btn" onClick={handleUpdatePayment}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        )}
      </LoadingModal>      

      {/* ✅ Confirm cancel modal */}
      {confirmOpen && (
        <div className="loading-modal-overlay" role="dialog" aria-modal="true" aria-label="Confirm cancellation">
          <div className="loading-modal">
            <button className="modal-close-btn" onClick={() => setConfirmOpen(false)} aria-label="Close">✖</button>
            <div className="membershipCard-card__section">
              <div className="membershipCard-card__row vertical-align">
                <div className="membershipCard-card__title">Canceling your membership today means</div>
              </div>
            </div>
            <div className="security-card__divider" />
            <div className="membershipCard-card__section">
              <div className="membershipCard-card__row vertical-align">
                <div className="membershipCard-card__desc">
                    Your subscription will end in {daysRemaining} {daysLabel}
                </div>
              </div>
              <div className="membershipCard-card__section">
                <div className="premium-box">
                  <Calendar className="confirm-calendar-icon" size={18} />
                  <div>
                    <div className="premium-desc">You've already paid for the {daysRemaining} {daysLabel} remaining on you subscription. <br></br>
                      After {formattedNextBilling || invoce?.nextBillingDate || 'your billing period'}, you'll lose access to premium apps and services.</div>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 12, alignItems: 'center' }}>
              <div style={{ display: 'flex', gap: 12, marginTop: 8 }}>
                <button className="btn edit-btn" onClick={() => setConfirmOpen(false)}>No, keep it</button>
                <button
                  className="btn cancel-btn"
                  onClick={() => {
                    setConfirmOpen(false);
                    handleSubmit();
                  }}
                >
                  Yes, cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>     
  );
};

export default MembershipCardCard;
